import "./TicketCard.css";
import { useContext, useState } from "react";
import { DeleteModal } from "../../modals/Delete";
import { deleteTicketAPI } from "../../../utils/api/teamTicket";
import { Context } from "../../../context/Context";
import { useRestaurantAccess } from "../../../access/hooks/useRestaurantAccess";
import { Button, Card, Tags, Calendar, Pencil, Timer, Trash2 } from "../../ui";
import { formatTicketDate } from "./ticketConstants";

const getStatusVariant = (status) => {
  if (["completed", "done", "team", "integration", "comment"].includes(status)) {
    return "success";
  }

  if (["inProgress", "incompleteData", "pending", "support"].includes(status)) {
    return "warning";
  }

  if (["rejected"].includes(status)) {
    return "danger";
  }

  if (["new", "client", "app", "manager"].includes(status)) {
    return "primary";
  }

  return "neutral";
};

const getDelayClass = (hours) => {
  if (hours >= 48) return "late-48";
  if (hours >= 24) return "late-24";
  if (hours >= 12) return "late-12";
  if (hours >= 8) return "late-8";
  return "";
};

export default function TicketCard({
  ticket,
  isSelected = false,
  onClick,
  onEdit,
  onDeleted,
}) {
  const { handleTokenExpiration } = useContext(Context);
  const { has, canFor } = useRestaurantAccess();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState("");

  const hoursSinceStatusChange =
    ticket.status === "done"
      ? 0
      : (Date.now() - new Date(ticket.statusChangedAt || ticket.createdAt)) /
        1000 /
        60 /
        60;

  const closeDeleteModal = () => {
    setDeleteTarget(null);
    setDeleteError("");
  };

  const handleDelete = async () => {
    // the ticket's restaurant must be one the user may act on
    if (!canFor("onboarding.ticket.delete", ticket.storeId?._id ?? ticket.storeId)) return;
    const token = localStorage.getItem("auth-token");
    if (!token) return handleTokenExpiration();

    const deleted = await deleteTicketAPI(token, ticket._id);
    if (deleted) {
      closeDeleteModal();
      onDeleted();
    } else {
      // Keep the dialog open so the failure is visible and can be retried.
      setDeleteError("The ticket could not be deleted. Please try again.");
    }
  };

  return (
    <>
      <div onClick={onClick} className="ticket-card-click">
        <Card
          hoverable
          className={`ticket-card ${ticket.status} ${getDelayClass(
            hoursSinceStatusChange,
          )} ${isSelected ? "selected" : ""}`}
        >
          <div className="ticket-card-header">
            <div className="ticket-card-tags">
              {ticket.scheduled?.isRecurring && (
                <Calendar size={16} aria-label="Scheduled" role="img" />
              )}

              <Tags
                size="sm"
                variant={getStatusVariant(ticket.processingStage)}
                appearance="soft"
                className={`ticket-card-status ${ticket.processingStage}`}
              >
                {ticket.processingStage}
              </Tags>

              <Tags
                size="sm"
                variant={getStatusVariant(ticket.status)}
                appearance="soft"
                className={`ticket-card-status ${ticket.status}`}
              >
                {ticket.status}
              </Tags>
            </div>

            <div className="ticket-card-store">
              {ticket.storeId?.businessName}
            </div>
          </div>

          <div className="ticket-card-body">
            <h3 className="ticket-card-title">
              {/* Keyboard entry point for opening the ticket; mouse users can
                  click anywhere on the card. */}
              <button
                type="button"
                className="ticket-card-open"
                aria-current={isSelected || undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  onClick();
                }}
              >
                {ticket.title}
              </button>
            </h3>
            {ticket.description && (
              <p className="ticket-card-desc">{ticket.description}</p>
            )}
          </div>

          <div className="ticket-card-info">
            <span>{ticket.createdBy?.name}</span>
            <span>{ticket.assignedTo?.name}</span>
          </div>

          <div className="ticket-card-info">
            <span>{formatTicketDate(ticket.createdAt)}</span>
          </div>

          {ticket.lastMessage?.message && (
            <div className="ticket-card-last-message">
              <strong>{ticket.lastMessage.senderName}</strong> <br />
              at {formatTicketDate(ticket.lastMessage.createdAt)} <br />
              <span className="last-message-text">
                {ticket.lastMessage.message}
              </span>
            </div>
          )}

          {ticket.statusChangedAt && (
            <div className="ticket-card-bottom">
              <div className="ticket-card-status-info">
                {ticket.statusChangedBy?.name}
              </div>
              <div className="ticket-card-status-info">
                {formatTicketDate(ticket.statusChangedAt)}
              </div>
            </div>
          )}

          <div className="ticket-card-bottom">
            <div
              className="ticket-card-delay-info"
              title="Hours since the last status change"
            >
              {hoursSinceStatusChange.toFixed(0)}{" "}
              <Timer height={15} width={15} aria-hidden="true" />
            </div>

            <div className="ticket-card-bottom">
              <Button
                variant="neutral"
                appearance="ghost"
                size="sm"
                rounded
                icon={<Pencil height={15} width={15} />}
                className="ticket-card-update-btn"
                aria-label={`Edit ticket ${ticket.title}`}
                title="Edit"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              />

              {has("onboarding.ticket.delete") && (
                <Button
                  variant="danger"
                  appearance="ghost"
                  size="sm"
                  rounded
                  icon={<Trash2 height={15} width={15} />}
                  className="ticket-card-delete-btn"
                  aria-label={`Delete ticket ${ticket.title}`}
                  title="Delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTarget(ticket._id);
                  }}
                />
              )}
            </div>
          </div>
        </Card>
      </div>

      {deleteTarget && (
        <DeleteModal
          cancel={closeDeleteModal}
          id={deleteTarget}
          handleDelete={handleDelete}
          error={deleteError}
        />
      )}
    </>
  );
}
